// ============================================================================
// Bid step 3 — Services. Built to Ben's approved demo, 2026-10-15
// (docs/build-room/demos/item-841.html; item #841, supersedes the one-line page
// of TCSD-0361). Nightly cleaning leads and is always in the contract. Three
// Yes/No switches decide what else is in the contract. Consumables and baked-in
// floor care add their own lines to the price; chemicals never change it.
// The only intended differences from the demo: the market is the bid's own from
// step 2 (no picker), and the baselines live in the pricing settings (no table).
// ⛔ Every dollar figure comes from bid_price_lines(); nothing here adds money up.
// ============================================================================

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, Skeleton } from '../../shared/components';
import {
  fetchFloorCareLines, fetchFloorServices, fetchPriceLines, fetchSettingFor, fetchSuggestion, saveServices,
  type BidRow, type PriceLines,
} from './api';
import { CustomerPricePanel } from './price-lines';
import {
  FLOOR_TYPES, FREQUENCIES, RATE_KEY, addedLine, answersFromBid, badgeKind, badgeText, cardRate, clamp, customerView,
  lineRate, marketShort, problems, toDraft, type Answers, type Baseline, type Bounds, type Line,
} from './services-logic';
import { SaveError } from './fields';
import { saveText } from './start-form';
import { money, num, perSf } from './format';
import { useBidLoad } from './use-bid-load';

interface Loaded {
  services: { code: string; name: string }[];
  bases: Record<string, Baseline>;
  bounds: Bounds;
  answers: Answers;
  nightlyNote: string;
  /** which saved version of the bid these answers came from */
  forKey: string;
}

const BADGE: Record<string, string> = {
  edited: 'bg-st-info-bg text-st-info-fg',
  market: 'bg-st-ok-bg text-st-ok-fg',
  default: 'bg-st-warn-bg text-st-warn-fg',
  none: 'bg-st-warn-bg text-st-warn-fg',
};
const NUM = 'w-[90px] rounded-md border border-tcs-border bg-tcs-field px-2 py-1 text-right text-sm text-tcs-text';
const SEL = 'rounded-md border border-tcs-border bg-tcs-field px-2 py-1 text-sm text-tcs-text';

function Badge({ kind, market }: { kind: string; market: string | null }) {
  return <span className={`mt-1 inline-block whitespace-nowrap rounded-full px-2 py-px text-[11px] ${BADGE[kind]}`}>{badgeText(kind as never, market)}</span>;
}

function YesNo({ value, onChange, disabled }: { value: boolean | null; onChange: (v: boolean) => void; disabled: boolean }) {
  const b = 'px-3.5 py-1 text-[13px] disabled:cursor-default';
  return (
    <span className="inline-flex flex-none overflow-hidden rounded-md border border-tcs-border">
      <button type="button" disabled={disabled} onClick={() => onChange(true)}
        className={`${b} ${value === true ? 'bg-st-ok-solid text-st-solid-ink' : 'bg-tcs-raised text-tcs-text'}`}>Yes</button>
      <button type="button" disabled={disabled} onClick={() => onChange(false)}
        className={`${b} ${value === false ? 'bg-tcs-text-muted text-tcs-bg' : 'bg-tcs-raised text-tcs-text'}`}>No</button>
    </span>
  );
}

function Service({ title, sub, value, onChange, disabled, hero, right, children }: {
  title: string; sub: string; value?: boolean | null; onChange?: (v: boolean) => void; disabled?: boolean; hero?: boolean; right?: ReactNode; children?: ReactNode;
}) {
  return (
    <div className={`mt-2.5 overflow-hidden rounded-lg border ${hero ? 'border-2 border-tcs-text' : 'border-tcs-border'}`}>
      <div className={`flex items-center justify-between gap-2.5 px-3 py-2.5 ${hero ? 'bg-tcs-bg' : 'bg-tcs-surface'}`}>
        <div><b className="text-[15px]">{title}</b><small className="block text-xs font-normal text-tcs-text-muted">{sub}</small></div>
        {right ?? (onChange && <YesNo value={value ?? null} onChange={onChange} disabled={!!disabled} />)}
      </div>
      {children && <div className="border-t border-tcs-border p-3">{children}</div>}
    </div>
  );
}

function Option({ on, name, value, title, desc, onPick, disabled, children }: {
  on: boolean; name: string; value: string; title: ReactNode; desc: string; onPick: () => void; disabled: boolean; children?: ReactNode;
}) {
  return (
    <label className={`my-1.5 block cursor-pointer rounded-lg border px-3 py-2 ${on ? 'border-tcs-text ring-1 ring-inset ring-tcs-text' : 'border-tcs-border'}`}>
      <input type="radio" className="mr-1.5" name={name} value={value} checked={on} disabled={disabled} onChange={onPick} />
      <b>{title}</b>
      <div className="ml-[22px] mt-0.5 text-[13px] text-tcs-text-muted">{desc}</div>
      {on && children && <div className="mb-0.5 ml-[22px] mt-2">{children}</div>}
    </label>
  );
}

async function load(bid: BidRow): Promise<Loaded> {
  const keys = ['consumables_pct_of_monthly_default', 'consumables_pct_of_monthly_min', 'consumables_pct_of_monthly_max',
    'consumables_cost_plus_markup_default', 'consumables_cost_plus_markup_min', 'consumables_cost_plus_markup_max'];
  const [services, lines, suggestion, ...vals] = await Promise.all([
    fetchFloorServices(),
    fetchFloorCareLines(bid.bid_id),
    fetchSuggestion(bid.bid_id),
    ...keys.map((k) => fetchSettingFor(bid, k)),
  ]);
  const pctOf = (i: number, fallback: number) => (vals[i]?.value != null ? Math.round(Number(vals[i]!.value) * 10000) / 100 : fallback);
  const bounds: Bounds = {
    pctDefault: pctOf(0, 15), pctMin: pctOf(1, 0), pctMax: pctOf(2, 100),
    markupDefault: pctOf(3, 20), markupMin: pctOf(4, 0), markupMax: pctOf(5, 100),
  };
  const bases: Record<string, Baseline> = {};
  await Promise.all(services.map(async (s) => {
    const key = RATE_KEY[s.code];
    const v = key ? await fetchSettingFor(bid, key) : null;
    bases[s.code] = { value: v ? Number(v.value) : null, fromMarket: !!v && !!bid.market_region && v.scope_label === bid.market_region };
  }));
  const asLine = (l: Awaited<ReturnType<typeof fetchFloorCareLines>>[number]): Line => ({
    line_id: l.line_id, floor_type: l.floor_type ?? '', service_code: l.service_code, sqft: l.sqft != null ? Number(l.sqft) : null,
    rate: Number(l.rate_per_sf), times_per_year: l.times_per_year ?? 2, edited: l.rate_edited,
  });

  // What the nightly line is, said the way the demo says it when the price is the formula's.
  const nights = bid.service_freq_per_week;
  const parts = [nights ? `${nights} nights` : null];
  const s = suggestion;
  const formula = s && bid.facility_type === 'Dialysis' && s.dialysis_rate_per_sf != null && bid.quoted_monthly != null
    && Number(bid.quoted_monthly) === Number(s.recommended_monthly) && !s.sqft_estimated
    // the small-site floor did not set the price
    && Number(s.recommended_monthly) > Number(s.small_site_floor ?? 0);
  if (formula) parts.push(`${num(s!.total_sqft)} sq ft × ${perSf(s!.dialysis_rate_per_sf)} dialysis ${nights}-night rate, rounded to ${money(s!.price_rounding)}`);
  else if (bid.quoted_monthly != null) parts.push('the price set on Pricing and Review');
  else parts.push('not priced yet');
  parts.push('always in the contract');

  return {
    services, bases, bounds,
    answers: answersFromBid(bid, lines.map(asLine), bounds),
    nightlyNote: parts.filter(Boolean).join(' · '),
    forKey: `${bid.bid_id}|${bid.updated_at}`,
  };
}

export function ServicesPanel({ bid, onNext }: { bid: BidRow; onNext: () => void }) {
  const locked = bid.status !== 'Draft' && bid.status !== 'Submitted';
  const { data, error } = useBidLoad<Loaded | null>(() => load(bid), [bid.bid_id, bid.updated_at], null);
  const [a, setA] = useState<Answers | null>(null);
  const [price, setPrice] = useState<PriceLines | null>(null);
  const [priceErr, setPriceErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // the screen starts from what is saved, and starts again only when the saved bid changes
  // (a re-read of the same bid must not throw away what is on screen)
  const startedFrom = useRef<string | null>(null);
  useEffect(() => {
    if (data && startedFrom.current !== data.forKey) { startedFrom.current = data.forKey; setA(data.answers); }
  }, [data]);

  const names = useMemo(() => new Map((data?.services ?? []).map((s) => [s.code, s.name])), [data]);
  const name = (code: string) => names.get(code) ?? code;
  const codes = useMemo(() => (data?.services ?? []).map((s) => s.code), [data]);
  const draft = useMemo(() => (a && data ? toDraft(a, data.bases, codes) : null), [a, data, codes]);

  // live price from the database, a moment after the last change
  useEffect(() => {
    if (!draft) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      fetchPriceLines([bid.bid_id], { ...draft.services, lines: draft.lines })
        .then((r) => { if (!cancelled) { setPrice(r[0] ?? null); setPriceErr(null); } })
        .catch((e) => { if (!cancelled) setPriceErr(saveText(e)); });
    }, 150);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [draft, bid.bid_id, bid.quoted_monthly]);

  if (error) return <p role="alert" className="text-sm text-st-danger-fg">{error}</p>;
  if (!data || !a) return <Skeleton className="h-96 w-full" />;

  const { bases, bounds } = data;
  const market = bid.market_region;
  const m = marketShort(market);
  const set = (patch: Partial<Answers>) => setA({ ...a, ...patch });
  const setLine = (i: number, patch: Partial<Line>) => set({ lines: a.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const view = customerView(a, name, bases);
  const blocked = problems(a, bases).length > 0;
  const year = price?.floor_care_yearly != null ? Number(price.floor_care_yearly) : null;

  async function priceIt() {
    if (locked) { onNext(); return; }
    setSaving(true); setErr(null);
    try {
      await saveServices(bid.bid_id, draft!.services, draft!.lines);
      onNext();
    } catch (e) { setErr(saveText(e)); } finally { setSaving(false); }
  }

  return (
    <div className="space-y-3">
      <SaveError text={err} />
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_330px]">
        <div className="rounded-[10px] border border-tcs-border bg-tcs-raised p-3.5">
          {/* NIGHTLY */}
          <Service hero title="Nightly cleaning" sub={data.nightlyNote}
            right={<div className="text-[22px] font-bold tabular-nums">{bid.quoted_monthly != null ? `${money(bid.quoted_monthly)}/mo` : '—'}</div>} />

          {/* CONSUMABLES */}
          <Service title="Consumables" sub="Paper, soap, liners: TCS supplies and restocks" value={a.cons} disabled={locked}
            onChange={(v) => set({ cons: v })}>
            {a.cons == null ? null : !a.cons ? (
              <div className="text-[13px] text-tcs-text-muted">Not in the contract. The customer supplies their own consumables, and the proposal says so.</div>
            ) : (
              <>
                <Option name="cm" value="pct" on={a.consMode === 'pct_of_monthly'} disabled={locked} onPick={() => set({ consMode: 'pct_of_monthly' })}
                  title={<>% of monthly <span className="ml-1 inline-block rounded-full bg-st-ok-bg px-2 py-px text-[11px] font-normal text-st-ok-fg">default</span></>}
                  desc="Covers supply cost, resupply, management and stocking. Shows as its own line on the bid.">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <input type="range" className="w-[220px] accent-tcs-text" min={bounds.pctMin} max={bounds.pctMax} step={0.5} value={a.pct} disabled={locked}
                      aria-label="Consumables, % of nightly cleaning" onChange={(e) => set({ pct: Number(e.target.value) })} />
                    <input type="number" className={NUM} min={bounds.pctMin} max={bounds.pctMax} step={0.5} defaultValue={a.pct} key={`pct-${a.pct}`} disabled={locked}
                      onBlur={(e) => set({ pct: clamp(Number(e.target.value), bounds.pctMin, bounds.pctMax, bounds.pctDefault) })} />%
                    <span className="text-[13px] text-tcs-text-muted">of nightly cleaning = <b>{money(price?.consumables_monthly)}</b>/mo · allowed {bounds.pctMin}–{bounds.pctMax}%</span>
                  </div>
                </Option>
                <Option name="cm" value="cost" on={a.consMode === 'cost_plus'} disabled={locked} onPick={() => set({ consMode: 'cost_plus' })}
                  title="Cost + %" desc="No estimate needed. When we buy your supplies, we invoice you cost plus a markup.">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <input type="range" className="w-[220px] accent-tcs-text" min={bounds.markupMin} max={bounds.markupMax} step={1} value={a.markup} disabled={locked}
                      aria-label="Consumables markup on cost" onChange={(e) => set({ markup: Number(e.target.value) })} />
                    <input type="number" className={NUM} min={bounds.markupMin} max={bounds.markupMax} step={1} defaultValue={a.markup} key={`mk-${a.markup}`} disabled={locked}
                      onBlur={(e) => set({ markup: clamp(Math.round(Number(e.target.value)), bounds.markupMin, bounds.markupMax, bounds.markupDefault) })} />%
                    <span className="text-[13px] text-tcs-text-muted">markup on cost · allowed {bounds.markupMin}–{bounds.markupMax}% · adds nothing to the monthly price</span>
                  </div>
                </Option>
                <Option name="cm" value="other" on={a.consMode === 'other'} disabled={locked} onPick={() => set({ consMode: 'other' })}
                  title="Other" desc="Any other arrangement. Needs an estimated monthly cost and a description.">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <div><span className="mb-0.5 block text-xs text-tcs-text-muted">Estimated monthly $ *</span>
                      <input type="number" className={NUM} value={a.otherAmt} placeholder="0" min={0} disabled={locked}
                        onChange={(e) => set({ otherAmt: e.target.value })} /></div>
                    <div className="min-w-[200px] flex-1"><span className="mb-0.5 block text-xs text-tcs-text-muted">Description *</span>
                      <input type="text" className={`${SEL} w-full`} value={a.otherDesc} placeholder="e.g. flat $300/mo, customer orders paper" disabled={locked}
                        onChange={(e) => set({ otherDesc: e.target.value })} /></div>
                  </div>
                  {(!a.otherAmt || !a.otherDesc.trim()) && <div className="text-[12.5px] text-st-danger-fg">Both fields are required before the bid can be priced.</div>}
                </Option>
              </>
            )}
          </Service>

          {/* CHEMICALS */}
          <Service title="Chemicals" sub="TCS brings and resupplies cleaning chemicals" value={a.chem} disabled={locked}
            onChange={(v) => set({ chem: v })}>
            {a.chem == null ? null : a.chem
              ? <div className="text-[13px] text-tcs-text-muted">In the contract: TCS supplies chemicals. <b>Never changes the price.</b></div>
              : <div className="text-[13px] text-tcs-text-muted">Not in the contract: the customer supplies chemicals (e.g. Fresenius). <b>Price is the same either way.</b></div>}
          </Service>

          {/* FLOOR */}
          <Service title="Periodic floor care" sub="Strip & wax, scrub & recoat, auto scrub, burnish, grout, carpet" value={a.floor} disabled={locked}
            onChange={(v) => set({ floor: v })}>
            {a.floor == null ? null : !a.floor ? (
              <div className="text-[13px] text-tcs-text-muted">No periodic floor care in the contract.</div>
            ) : (
              <>
                <Option name="fm" value="baked" on={a.floorMode === 'baked_in'} disabled={locked} onPick={() => set({ floorMode: 'baked_in' })}
                  title="Baked into price" desc="Each floor-care line has a yearly cost. The yearly total is spread across the monthly price." />
                <Option name="fm" value="sep" on={a.floorMode === 'billed_separately'} disabled={locked} onPick={() => set({ floorMode: 'billed_separately' })}
                  title="Agree to periodic floor care, priced separately" desc="The contract commits to floor care at the rates below, billed per visit as performed. Doesn't change the monthly price." />
                {a.floorMode === 'baked_in' ? (
                  <>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full border-collapse text-[13.5px]">
                        <thead><tr className="border-b border-tcs-border text-left text-[11.5px] font-semibold text-tcs-text-muted">
                          <th className="whitespace-nowrap px-1.5 py-1">Floor type</th><th className="whitespace-nowrap px-1.5 py-1">Service</th>
                          <th className="whitespace-nowrap px-1.5 py-1 text-right">Sq ft</th><th className="whitespace-nowrap px-1.5 py-1 text-right">$/sq ft</th>
                          <th className="whitespace-nowrap px-1.5 py-1">Frequency</th><th className="whitespace-nowrap px-1.5 py-1 text-right">Per year</th><th />
                        </tr></thead>
                        <tbody>
                          {a.lines.map((l, i) => {
                            const rate = lineRate(l, bases[l.service_code]);
                            // the line's own product, shown beside it; the price itself is the database's
                            const yr = (l.sqft ?? 0) * (rate ?? 0) * l.times_per_year;
                            return (
                              <tr key={i} className="border-b border-tcs-border align-middle">
                                <td className="px-1.5 py-1.5"><select className={SEL} value={l.floor_type} disabled={locked} aria-label="Floor type"
                                  onChange={(e) => setLine(i, { floor_type: e.target.value })}>{FLOOR_TYPES.map((f) => <option key={f}>{f}</option>)}</select></td>
                                <td className="px-1.5 py-1.5"><select className={SEL} value={l.service_code} disabled={locked} aria-label="Service"
                                  onChange={(e) => setLine(i, { service_code: e.target.value, edited: false, rate: null })}>
                                  {data.services.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></td>
                                <td className="px-1.5 py-1.5 text-right"><input type="number" className={NUM} min={0} step={100} disabled={locked} aria-label="Sq ft"
                                  value={l.sqft ?? ''} placeholder="required" onChange={(e) => setLine(i, { sqft: e.target.value === '' ? null : Number(e.target.value) || 0 })} /></td>
                                <td className="px-1.5 py-1.5 text-right"><input type="number" className={NUM} min={0} step={0.05} disabled={locked} aria-label="$/sq ft"
                                  defaultValue={rate == null ? '' : rate.toFixed(2)} key={`r-${i}-${l.service_code}-${l.edited}-${rate}`} placeholder="required"
                                  onBlur={(e) => {
                                    if (e.target.value === (rate == null ? '' : rate.toFixed(2))) return;
                                    // a cleared box is no rate (red, cannot price), never $0.00
                                    setLine(i, { rate: e.target.value === '' ? null : Number(e.target.value) || 0, edited: true });
                                  }} />
                                  <br /><Badge kind={badgeKind(l.edited, bases[l.service_code])} market={market} /></td>
                                <td className="px-1.5 py-1.5"><select className={SEL} value={l.times_per_year} disabled={locked} aria-label="Frequency"
                                  onChange={(e) => setLine(i, { times_per_year: Number(e.target.value) })}>
                                  {FREQUENCIES.map((f) => <option key={f.times} value={f.times}>{f.label}</option>)}</select></td>
                                <td className="whitespace-nowrap px-1.5 py-1.5 text-right tabular-nums">{money(yr)}</td>
                                <td className="px-1.5 py-1.5">{a.lines.length > 1 && !locked && (
                                  <button type="button" className="px-1.5 text-base text-tcs-text-muted" title="Remove" aria-label="Remove"
                                    onClick={() => set({ lines: a.lines.filter((_, j) => j !== i) })}>×</button>)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2.5">
                      {!locked ? <button type="button" className="rounded-md border border-tcs-border bg-tcs-raised px-2.5 py-1 text-[13px]" onClick={() => set({ lines: [...a.lines, addedLine()] })}>+ Add a floor-care line</button> : <span />}
                      <div className="text-sm"><b>{money(year)}</b> a year ÷ 12 = <b>{money(price?.floor_care_monthly)}</b> added to the monthly price</div>
                    </div>
                    {a.lines.some((l) => lineRate(l, bases[l.service_code]) == null && badgeKind(false, bases[l.service_code]) === 'none') && (
                      <div className="mt-1.5 text-[12.5px] text-st-danger-fg">Burnish and grout restoration have no rate anywhere in our records yet. Type one in before the bid can be priced.</div>
                    )}
                    {a.lines.some((l) => !l.edited && badgeKind(false, bases[l.service_code]) === 'default') && (
                      <div className="mt-2 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-[13.5px] text-st-warn-fg">
                        No {m} figure for some lines, so the company default is used. The BDD and the market&apos;s AM set the rate together and type it in. Nothing blocks the bid.
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full border-collapse text-[13.5px]">
                        <thead><tr className="border-b border-tcs-border text-left text-[11.5px] font-semibold text-tcs-text-muted">
                          <th className="px-1.5 py-1">Service in the contract</th><th className="px-1.5 py-1 text-right">$/sq ft per visit</th></tr></thead>
                        <tbody>
                          {data.services.map((s) => {
                            const ed = a.card[s.code] !== undefined;
                            const v = cardRate(a, s.code, bases[s.code]);
                            return (
                              <tr key={s.code} className="border-b border-tcs-border">
                                <td className="px-1.5 py-1.5">{s.name}</td>
                                <td className="px-1.5 py-1.5 text-right"><input type="number" className={NUM} min={0} step={0.05} placeholder="required" disabled={locked}
                                  aria-label={`${s.name}, $/sq ft per visit`} defaultValue={v == null ? '' : Number(v).toFixed(2)} key={`c-${s.code}-${ed}-${v}`}
                                  onBlur={(e) => {
                                    if (e.target.value === (v == null ? '' : Number(v).toFixed(2))) return;
                                    const card = { ...a.card };
                                    // a cleared box goes back to the saved or baseline figure, never to $0.00
                                    if (e.target.value === '') delete card[s.code]; else card[s.code] = Number(e.target.value) || 0;
                                    set({ card });
                                  }} />
                                  <br /><Badge kind={badgeKind(ed, bases[s.code])} market={market} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-1.5 text-[13px] text-tcs-text-muted">Billed per visit as performed. Monthly price unchanged.</div>
                  </>
                )}
              </>
            )}
          </Service>
        </div>

        <div>
          <CustomerPricePanel price={price} view={view} />
          {priceErr && <p role="alert" className="mt-2 text-xs text-st-danger-fg">{priceErr}</p>}
        </div>
      </div>
      <div className="flex justify-end"><Button onClick={priceIt} loading={saving} disabled={!locked && blocked}>Price it</Button></div>
    </div>
  );
}
